/**
 * The Commercial Audits nav group.
 *
 * Renders the canonical pipeline from the catalog served by
 * GET /api/pipeline/steps, merged with per-asset status from
 * GET /api/pipeline/{client_id}/steps. There is no step list in this file:
 * adding a step to backend/pipeline_steps.py adds it to the nav.
 *
 * Gating is SOFT (PIPELINE_DECISIONS.md decision 3): every row is clickable
 * regardless of status, and a blocked row states why instead of refusing the
 * click.
 *
 * Step 6 (Pulse Sync) never completes, so it renders cycle progress and a cycle
 * count and never a done badge. That is driven by the catalog's `terminal`
 * flag, NOT by matching the step key — a second never-completing step must not
 * require editing this file.
 *
 * COMMERCIAL ONLY. The residential group does not use this component.
 */
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";
import {
  componentIcon, componentLabel, componentTestId, cycleProgressPercent,
  statusBadgeClass, stepIcon, stepNavLabel, stepTestId,
} from "@/utils/pipelineNav";

/** Why a step is blocked: the stored reason, else its unmet prerequisites. */
const BlockedReason = ({ step, t }) => {
  if (step.status !== "blocked") return null;

  // A stored reason wins — a writer knows more than the prerequisite graph does.
  if (step.blocked_reason) {
    return (
      <span className="pipeline-nav-reason" data-testid={`pipeline-blocked-reason-${step.key}`}>
        {step.blocked_reason}
      </span>
    );
  }

  const details = step.unmet_prerequisite_details || [];
  if (details.length === 0) return null;

  // The reason code comes from the PREREQUISITE step, so Pulse Sync reads as
  // "has not completed a cycle yet" and never as "is not complete" — a
  // statement that would never stop being true.
  const phrase = (detail) =>
    `${stepNavLabel(t, detail)} ${t(`pipeline_reason_${detail.reason_code}`)}`;

  return (
    <span className="pipeline-nav-reason" data-testid={`pipeline-blocked-reason-${step.key}`}>
      {t("pipeline_blocked_waiting_on")} {details.map(phrase).join(", ")}
    </span>
  );
};

/**
 * Cycle progress for a non-terminal step.
 *
 * The bar is always mid-flight because a new cycle is always accumulating, and
 * the count is completed cycles. Prorated months surface here as an anomaly
 * marker: a scaled month is a different quality of input from a full one and
 * must be visible to every consumer of the cycle's output, the nav included.
 */
const CycleProgress = ({ step, t }) => {
  const cycle = step.cycle || {};
  const prorated = (cycle.prorated_months || []).length;
  const stateLabel = step.cycle_state === "validated_and_training"
    ? t("pulse_state_validated_training")
    : t("pulse_state_accumulating");

  return (
    <span className="pipeline-nav-cycle" data-testid={`pipeline-cycle-${step.key}`}>
      <span className="progress-bar pipeline-cycle-bar">
        <span
          className="progress-fill pipeline-cycle-fill"
          style={{ width: `${cycleProgressPercent(cycle)}%` }}
          data-testid={`pipeline-cycle-bar-${step.key}`}
        ></span>
      </span>
      <span className="pipeline-nav-cycle-meta">
        <span
          className="pipeline-cycle-state"
          data-testid={`pipeline-cycle-state-${step.key}`}
        >
          {stateLabel}
        </span>
        {prorated > 0 && (
          <span
            className="pipeline-cycle-anomaly"
            title={t("pulse_scaled_months")}
            data-testid={`pipeline-cycle-anomaly-${step.key}`}
          >
            <AlertTriangle size={11} />
            {prorated}
          </span>
        )}
      </span>
    </span>
  );
};

/**
 * One row: icon, then a stack of a full (wrapping) label, a status line, and
 * any sublines. Label and status are on separate lines because at the 260px
 * sidebar width they cannot share one without the label being clipped.
 */
const PipelineNavRow = ({
  step, label, Icon, active, onClick, testId, indented, parallelMarker, t,
}) => {
  // A step with no view is not built yet. Still clickable — it opens a stub that
  // states as much — but visibly distinct from a step you can actually work in.
  const notBuilt = step.implemented === false && !step.parallel;
  const nonTerminal = step.terminal === false;
  const cycle = step.cycle || {};

  const classes = [
    "nav-item", "pipeline-nav-item",
    active ? "active" : "",
    notBuilt ? "not-built" : "",
    indented ? "pipeline-nav-child" : "",
  ].filter(Boolean).join(" ");

  return (
    <div
      className={classes}
      onClick={onClick}
      data-testid={testId}
      aria-disabled={notBuilt ? "true" : undefined}
    >
      {parallelMarker && <span className="pipeline-parallel-marker" aria-hidden="true">∥</span>}
      <span className="nav-item-icon"><Icon size={20} /></span>
      <span className="pipeline-nav-body">
        {/* The title owns the full width of the row and wraps. A step label is
            the one thing a nav row cannot afford to abbreviate — a truncated
            "Consolidated…" does not identify the step — so nothing shares the
            line with it except the cycle chip, which is two glyphs wide. */}
        <span className="pipeline-nav-title-row">
          <span className="pipeline-nav-title">{label}</span>

          {/* Never a done badge for a step with no terminal state: a cycle
              count instead, because completed cycles is the only progress
              Pulse Sync has. */}
          {nonTerminal && (
            <span
              className="pipeline-nav-pill cycles"
              title={t("pulse_cycles_complete")}
              data-testid={`pipeline-cycle-count-${step.key}`}
            >
              <RefreshCw size={10} />
              {cycle.cycles_complete || 0}
            </span>
          )}
        </span>

        {/* Status sits on its own line beneath the label. It stays full text
            rather than a colour dot: status is the reason to read the row, and
            the translations ("non démarré", "No desarrollado") are too long to
            fit beside a label in any language. */}
        {(notBuilt || !nonTerminal) && (
          <span className="pipeline-nav-meta-row">
            {notBuilt ? (
              <span
                className="pipeline-nav-pill not-built"
                data-testid={`pipeline-not-built-${step.key}`}
              >
                {t("pipeline_not_built")}
              </span>
            ) : (
              <span
                className={`nav-item-badge pipeline-nav-status ${statusBadgeClass(step.status)}`}
                data-testid={`pipeline-status-${step.key}`}
              >
                {t(`pipeline_status_${step.status || "not_started"}`)}
              </span>
            )}
          </span>
        )}

        {nonTerminal && <CycleProgress step={step} t={t} />}
        <BlockedReason step={step} t={t} />
      </span>
    </div>
  );
};

const PipelineNav = ({ steps, currentView, stubStepKey, onSelect }) => {
  const t = useT();

  // Ordering comes from the catalog. `order` is the authoritative sequence
  // position, so it is sorted on rather than trusting array order.
  const ordered = [...(steps || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  const isActive = (target) => {
    if (target.view_key) return currentView === target.view_key;
    return currentView === "pipeline_step" && stubStepKey === target.key;
  };

  return (
    <>
      {ordered.map((step) => (
        <div key={step.key} className="pipeline-nav-step">
          <PipelineNavRow
            step={step}
            label={stepNavLabel(t, step)}
            Icon={stepIcon(step.key)}
            active={isActive(step)}
            onClick={() => onSelect(step)}
            testId={stepTestId(step.key)}
            t={t}
          />

          {/* A parallel step is ONE nav entry holding its components. Both are
              always visible and marked parallel — the components run alongside
              each other, and a disclosure that showed one at a time would read
              as a sequence. They carry no position numbers for the same
              reason: the step occupies a single place in the sequence. */}
          {step.parallel && (step.components || []).length > 0 && (
            <div
              className="pipeline-nav-parallel"
              data-testid={`pipeline-parallel-group-${step.key}`}
            >
              <div className="pipeline-nav-parallel-label">{t("pipeline_runs_in_parallel")}</div>
              {(step.components || []).map((component) => (
                <PipelineNavRow
                  key={component.key}
                  step={component}
                  label={componentLabel(t, component)}
                  Icon={componentIcon(component.key)}
                  active={isActive(component)}
                  onClick={() => onSelect(component, step)}
                  testId={componentTestId(component.key)}
                  indented
                  parallelMarker
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
};

export default PipelineNav;
